import { useMemo, useState, type ReactNode } from "react";
import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
  type ColumnDef,
  type RowSelectionState,
  type SortingState,
  type Table as TanstackTable,
} from "@tanstack/react-table";
import { ArrowDown, ArrowUp, ArrowUpDown, ChevronLeft, ChevronRight, Download, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { exportToCsv } from "@/lib/export-csv";
import { cn } from "@/lib/utils";

export type DataGridDensity = "compact" | "comfortable";

export type DataGridProps<T> = {
  data: T[];
  columns: ColumnDef<T, unknown>[];
  isLoading?: boolean;

  searchable?: boolean;
  searchPlaceholder?: string;

  enableRowSelection?: boolean;
  bulkActions?: (selected: T[], clear: () => void) => ReactNode;

  pageSize?: number;
  showPagination?: boolean;

  initialDensity?: DataGridDensity;
  showDensityToggle?: boolean;

  exportFilename?: string;

  onRowClick?: (row: T) => void;
  getRowId?: (row: T) => string;

  toolbar?: ReactNode;
  toolbarClassName?: string;

  emptyText?: string;
  className?: string;
  testId?: string;
};

const DENSITY_PADDING: Record<DataGridDensity, string> = {
  compact: "py-1.5",
  comfortable: "py-3",
};

function SortIndicator({ direction }: { direction: false | "asc" | "desc" }) {
  if (direction === "asc") return <ArrowUp className="h-3 w-3 ml-1" />;
  if (direction === "desc") return <ArrowDown className="h-3 w-3 ml-1" />;
  return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
}

function csvCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (value instanceof Date) return value.toISOString();
  return JSON.stringify(value);
}

function exportCurrentView<T>(table: TanstackTable<T>, filename: string) {
  const visibleColumns = table.getAllLeafColumns().filter(c => c.getIsVisible() && c.id !== "__select__");
  const headers = visibleColumns.map(c => {
    const header = c.columnDef.header;
    if (typeof header === "string") return header;
    return c.id;
  });
  const rows = table.getFilteredRowModel().rows.map(row =>
    visibleColumns.map(col => {
      const cell = row.getAllCells().find(c => c.column.id === col.id);
      const value = cell?.getValue();
      return csvCell(value);
    }),
  );
  exportToCsv(filename.endsWith(".csv") ? filename : `${filename}.csv`, headers, rows);
}

export function DataGrid<T>({
  data,
  columns,
  isLoading,
  searchable = true,
  searchPlaceholder = "Search…",
  enableRowSelection = false,
  bulkActions,
  pageSize = 25,
  showPagination = true,
  initialDensity = "comfortable",
  showDensityToggle = true,
  exportFilename,
  onRowClick,
  getRowId,
  toolbar,
  toolbarClassName,
  emptyText = "No results",
  className,
  testId,
}: DataGridProps<T>) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState<SortingState>([]);
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [density, setDensity] = useState<DataGridDensity>(initialDensity);

  const allColumns = useMemo<ColumnDef<T, unknown>[]>(() => {
    if (!enableRowSelection) return columns;
    const selectColumn: ColumnDef<T, unknown> = {
      id: "__select__",
      enableSorting: false,
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected() || (table.getIsSomePageRowsSelected() && "indeterminate")}
          onCheckedChange={value => table.toggleAllPageRowsSelected(!!value)}
          onClick={e => e.stopPropagation()}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={value => row.toggleSelected(!!value)}
          onClick={e => e.stopPropagation()}
          aria-label="Select row"
        />
      ),
    };
    return [selectColumn, ...columns];
  }, [columns, enableRowSelection]);

  const table = useReactTable({
    data,
    columns: allColumns,
    state: { globalFilter, sorting, rowSelection },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onRowSelectionChange: setRowSelection,
    enableRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : undefined,
    initialState: { pagination: { pageSize } },
    getRowId: getRowId ? (row, index) => getRowId(row) || String(index) : undefined,
  });

  const selectedRows = table.getSelectedRowModel().rows.map(r => r.original);
  const clearSelection = () => setRowSelection({});

  const totalRows = table.getFilteredRowModel().rows.length;
  const pagination = table.getState().pagination;
  const pageStart = totalRows === 0 ? 0 : pagination.pageIndex * pagination.pageSize + 1;
  const pageEnd = Math.min((pagination.pageIndex + 1) * pagination.pageSize, totalRows);

  return (
    <Card className={className} data-testid={testId}>
      <div className={cn("flex flex-wrap items-center gap-2 p-3 border-b", toolbarClassName)}>
        {searchable ? (
          <div className="relative flex-1 min-w-[180px] max-w-xs">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={globalFilter}
              onChange={e => setGlobalFilter(e.target.value)}
              placeholder={searchPlaceholder}
              className="h-8 text-xs pl-8"
            />
          </div>
        ) : null}

        {toolbar ? <div className="flex items-center gap-2 flex-wrap">{toolbar}</div> : null}

        <div className="flex-1" />

        {enableRowSelection && bulkActions && selectedRows.length > 0 ? (
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{selectedRows.length} selected</span>
            {bulkActions(selectedRows, clearSelection)}
          </div>
        ) : null}

        {showDensityToggle ? (
          <ToggleGroup
            type="single"
            value={density}
            onValueChange={v => v && setDensity(v as DataGridDensity)}
            size="sm"
            className="h-8"
          >
            <ToggleGroupItem value="compact" className="h-8 px-2 text-[10px]">Compact</ToggleGroupItem>
            <ToggleGroupItem value="comfortable" className="h-8 px-2 text-[10px]">Roomy</ToggleGroupItem>
          </ToggleGroup>
        ) : null}

        {exportFilename ? (
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            onClick={() => exportCurrentView(table, exportFilename)}
            disabled={totalRows === 0}
          >
            <Download className="h-3 w-3 mr-1" /> Export CSV
          </Button>
        ) : null}
      </div>

      <CardContent className="p-0 overflow-x-auto">
        {isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <Skeleton key={i} className="h-9 w-full" />
            ))}
          </div>
        ) : totalRows === 0 ? (
          <div className="py-12 text-center text-sm text-muted-foreground">{emptyText}</div>
        ) : (
          <Table>
            <TableHeader>
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    const canSort = header.column.getCanSort();
                    const sortDir = header.column.getIsSorted();
                    return (
                      <TableHead key={header.id} style={{ width: header.getSize() ? header.getSize() : undefined }}>
                        {header.isPlaceholder ? null : canSort ? (
                          <button
                            type="button"
                            className="flex items-center text-xs font-medium hover:text-foreground"
                            onClick={header.column.getToggleSortingHandler()}
                          >
                            {flexRender(header.column.columnDef.header, header.getContext())}
                            <SortIndicator direction={sortDir} />
                          </button>
                        ) : (
                          flexRender(header.column.columnDef.header, header.getContext())
                        )}
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {table.getRowModel().rows.map(row => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() ? "selected" : undefined}
                  className={cn(onRowClick && "cursor-pointer hover:bg-muted/50")}
                  onClick={() => onRowClick?.(row.original)}
                >
                  {row.getVisibleCells().map(cell => (
                    <TableCell key={cell.id} className={DENSITY_PADDING[density]}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {showPagination && totalRows > pagination.pageSize ? (
        <div className="flex items-center justify-between gap-3 p-3 border-t text-xs text-muted-foreground">
          <span>
            {pageStart}–{pageEnd} of {totalRows}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
            </Button>
            <span className="px-2">
              Page {pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
      ) : null}
    </Card>
  );
}

export default DataGrid;
